import { useState, useEffect, useRef } from "react"
import classNames from "classnames"
import { observer } from "mobx-react"
import Image from "next/image"
import YouTube from "react-youtube"
import { useStore } from "@features/common"
import { YouTubeBorder, SOCIAL_POST_CLIPPATH_ID } from "@features/journal"
import { Slider, SliderSlide } from "@ui"
import { IconYoutube, IconPlay, IconPlayFilled } from "@lib/icons"

import type { Options as YoutubeOptions } from "react-youtube"
import type { YoutubeVideoModel } from "@providers"

const YOUTUBE_URL = "https://www.youtube.com/channel/UCsB0J1spkPDp4ZB4_gnBWww"

export const YoutubeCarousel = observer(function YoutubeCarousel() {
  const { BlogModel, ResponsiveModel } = useStore()

  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null)
  const [isWindows, setIsWindows] = useState<boolean>(false)

  useEffect(() => {
    setIsWindows(ResponsiveModel.isWindowsOS)
  }, [ResponsiveModel.isWindowsOS])

  if (!BlogModel.enabledYoutube) return null

  return (
    <section className="relative" data-test-id="youtube_section">
      <YouTubeBorder className="absolute text-white w-full h-3.5 -top-3.5" />
      <div className="container pt-11 pb-20 px-0 tablet:pt-13 tablet:pb-25 laptop:px-22 desktop:pb-30 desktop-l:px-20">
        <div className="flex px-4 items-center mb-12 tablet:px-6 laptop:justify-center">
          <a
            href={YOUTUBE_URL}
            target="_blank"
            className={classNames(
              "space-x-4 tablet:space-x-5 inline-flex text-2xl tablet:text-4xl uppercase tracking-2 transition-opacity duration-150 ease-in hover:opacity-60",
              isWindows && "items-center",
            )}
            rel="noreferrer"
            data-test-id="youtube_section__title"
          >
            <span className="py-1">#purestories</span>
            <IconYoutube />
          </a>
        </div>
        <Slider
          options={{
            spaceBetween: -8,
            slidesPerView: "auto",
            navigation: true,
            breakpoints: {
              600: {
                spaceBetween: -36,
              },
              1024: {
                slidesPerView: 2,
                spaceBetween: 32,
              },
              1280: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
            },
          }}
        >
          {videos.map((item) => (
            <SliderSlide
              key={item.id}
              className="w-auto flex justify-center px-4 tablet-s:px-6 laptop:px-0"
            >
              <YoutubeVideo
                data={item}
                currentVideoId={currentVideoId}
                onPlay={() => setCurrentVideoId(item.id)}
              />
            </SliderSlide>
          ))}
        </Slider>
      </div>
    </section>
  )
})

const YOUTUBE_OPTIONS = {
  height: "100%",
  width: "100%",
  playerVars: {
    autoplay: 1,
    controls: 1,
    enablejsapi: 1,
    rel: 0,
    mute: 1,
  },
} as YoutubeOptions

type YoutubeVideoProps = {
  currentVideoId: string | null
  data: YoutubeVideoModel
  onPlay: () => void
}

function YoutubeVideo(props: YoutubeVideoProps) {
  const { currentVideoId, data, onPlay } = props

  const player = useRef(null)
  const [isVideoShown, setVideoShown] = useState(false)

  useEffect(() => {
    if (currentVideoId !== data.id) {
      // @ts-ignore
      player.current?.internalPlayer?.pauseVideo()
    }
  }, [currentVideoId, data.id])

  function ready(event) {
    event.target.playVideo()
    onPlay()
  }

  function play(event) {
    event.target.playVideo()
    onPlay()
  }

  function pause(event) {
    event.target.pauseVideo()
  }

  return (
    <div className="relative aspect-video group">
      <div
        className="h-[230px] flex items-center bg-black will-change-transform tablet-s:w-112 laptop:w-96 desktop:w-auto"
        style={{
          clipPath: `url(#${SOCIAL_POST_CLIPPATH_ID})`,
        }}
      >
        {isVideoShown ? (
          <YouTube
            ref={player}
            videoId={data.id}
            opts={YOUTUBE_OPTIONS}
            onReady={ready}
            onPlay={play}
            onPause={pause}
            className="w-full aspect-video"
            containerClassName="w-full aspect-auto"
          />
        ) : (
          <div
            onClick={() => setVideoShown(true)}
            className="relative w-full h-full inset-0 flex items-center justify-center cursor-pointer"
            data-test-id="youtube_section__play"
          >
            <div className="absolute z-1 inset-0 bg-black bg-opacity-40" />
            <div className="relative w-full h-full">
              <Image
                src={data.image}
                layout="fill"
                objectFit="cover"
                unoptimized={true}
              />
            </div>
            <button className="absolute z-2 text-white">
              <IconPlay className="block group-hover:hidden" />
              <IconPlayFilled className="hidden group-hover:block" />
            </button>
          </div>
        )}
      </div>
      <span className="block mt-4 font-serif text-xl leading-[30px] tablet:text-2xl text-black cursor-pointer hover:underline">
        {data.title}
      </span>
    </div>
  )
}

const videos = [
  {
    id: "66dO7L_1Ya4",
    title: "HOOKUP STORY 7 //Mayan//",
    image:
      "https://i.ytimg.com/vi/66dO7L_1Ya4/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLDX1lsV4yPcIAdUu5X2AMY94D4g8A",
  },
  {
    id: "U6c0CL5ifHM",
    title: "HOOKUP STORY 6  //James//",
    image:
      "https://i.ytimg.com/vi/U6c0CL5ifHM/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLAN1jN4hAec06VX16wLyvVPHAmRZA",
  },
  {
    id: "x17cTXdv-ko",
    title: "HOOKUP STORY 5  //James//",
    image:
      "https://i.ytimg.com/vi/x17cTXdv-ko/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLCfPzcUyHWtTjLTywre5x8za09uiQ",
  },
  {
    id: "GWI5-qxuAVs",
    title: "HOOKUP STORY 4  //Chloe//",
    image:
      "https://i.ytimg.com/vi/GWI5-qxuAVs/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLCsePmUlpO_rZaQf_q4lRXKcnSOJQ",
  },
  {
    id: "21sng7GKPw0",
    title: "HOOKUP STORY 3  //Aton//",
    image:
      "https://i.ytimg.com/vi/21sng7GKPw0/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLCwy70QTceetvU-RhzCOGwph_UdLw",
  },
  {
    id: "qM2kHNDqwwA",
    title: "HOOKUP STORY 2  //Jessica//",
    image:
      "https://i.ytimg.com/vi/qM2kHNDqwwA/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLAocVjyLOvT_bJPxQCYQKVGXH7gng",
  },
  {
    id: "rpaKbO5H_ro",
    title: "HOOKUP STORY 1  //Danielle//",
    image:
      "https://i.ytimg.com/vi/rpaKbO5H_ro/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLBmiwNbHICYlLSaCgbkzGUN820LvA",
  },
  {
    id: "gpFToYigRSs",
    title: "REAL HOOKUP STORIES 2017",
    image:
      "https://i.ytimg.com/vi/gpFToYigRSs/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLBOdsZZx2qAw4M5FAclWInmva-ojA",
  },
]
